import { useEffect, useState } from "react"
import {
    SOCKET_EVENTS,
    type PlayerIndexUpdatedBroadcast,
    type PlayerLeftBroadcast,
    type PlayerMoveClickedBroadcast,
    type PlayerMovedBroadcast,
    type PlayerSnapshot,
    type RoomStatePayload,
    type SpawnEventBroadcast,
} from "@domo/shared"
import { connectSocket, disconnectSocket } from "../lib/socket"
import { useNetworkStore } from "../stores/useNetworkStore"
import { usePlayerStore } from "../stores/usePlayerStore"

interface SocketState {
    connected: boolean
    roomId: string | null
    error: string | null
    peerCount: number
}

// Sprint 6 v2 — Tiempo de espera tras ROOM_STATE antes de mostrar la
// intro narrativa del orbe. Si el server tiene firma persistida del
// Tripulante, el SPAWN_EVENT auto llega en <500ms y el voidStage
// "checking" no se vuelve "intro" porque phase ya pasó a "descending".
// Si no hay firma, este timeout decide el momento en que aparece la
// narrativa. Suficiente para cubrir un fetch lento a Supabase sin
// dejar al Tripulante mirando un void vacío demasiado tiempo.
const VOID_INTRO_DELAY_MS = 1500

export function useSocket(token: string | null): SocketState {
    const [connected, setConnected] = useState(false)
    const [roomId, setRoomId] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)
    const peerCount = useNetworkStore((s) => s.peers.size)

    useEffect(() => {
        if (!token) return

        const socket = connectSocket(token)
        const net = useNetworkStore.getState()
        const player = usePlayerStore.getState()

        const onConnect = () => {
            setConnected(true)
            setError(null)
        }
        const onDisconnect = () => setConnected(false)
        const onConnectError = (err: Error) => {
            setError(err.message)
            setConnected(false)
        }

        const onRoomState = (payload: RoomStatePayload) => {
            setRoomId(payload.roomId)
            net.setSelfId(payload.selfId)
            net.setRoom(payload.roomId)

            const myself = payload.players.find(
                (p) => p.id === payload.selfId
            )
            const others = payload.players.filter(
                (p) => p.id !== payload.selfId
            )
            net.setPeers(others)

            // Hidratar mi propio jugador desde la snapshot autoritativa del server.
            if (myself) {
                player.setIdentity(myself.id, myself.displayName)
                player.setPosition(myself.position)
                player.setSiliconIndex(myself.siliconIndex)
                player.setAnchored(Boolean(myself.isAnchored))
                // Sprint 3/5 — Si reconectamos a una sesión donde ya
                // estábamos encarnados, restauramos directo la fase
                // "incarnated". Si no, queda en "void" del INITIAL y
                // esperamos a que llegue SPAWN_EVENT (auto o manual).
                if (myself.isIncarnated && myself.model && myself.polarity) {
                    player.setModel(myself.model)
                    player.setPolarity(myself.polarity)
                    player.completeIncarnation()
                    return
                }
            }

            // Sprint 6 v2 — voidStage arranca en "checking". Si en
            // VOID_INTRO_DELAY_MS no llegó SPAWN_EVENT auto desde el
            // server (que lo dispara la rama de fetchAvatarSignature),
            // pasamos a "intro" para que el Tripulante vea el orbe y
            // la narrativa. Si llegó antes, phase ya es "descending"
            // y este timer no aplica (chequeamos antes de mutar).
            window.setTimeout(() => {
                const cur = usePlayerStore.getState()
                if (cur.phase === "void" && cur.voidStage === "checking") {
                    usePlayerStore.setState({ voidStage: "intro" })
                }
            }, VOID_INTRO_DELAY_MS)
        }

        const onSpawnEvent = (b: SpawnEventBroadcast) => {
            const selfId = useNetworkStore.getState().selfId
            if (b.id === selfId) {
                // Soy yo: arrancamos animación local de descenso. El
                // selector se desmonta solo (lee phase del store).
                player.beginDescent(
                    b.model,
                    b.polarity,
                    b.position,
                    b.descentMs,
                    b.materializeMs,
                    b.auto
                )
                // El componente de la animación pasa por las fases
                // descending → materializing → incarnated con timers
                // cliente-side; al final llama a completeIncarnation().
                window.setTimeout(() => {
                    usePlayerStore.getState().advanceToMaterializing()
                }, b.descentMs)
                window.setTimeout(
                    () => {
                        usePlayerStore.getState().completeIncarnation()
                    },
                    b.descentMs + b.materializeMs
                )
                return
            }
            // Peer remoto: registramos la animación en el store de red
            // para que RemoteAvatar pinte el haz cayendo y materialice.
            net.beginPeerDescent(
                b.id,
                b.model,
                b.polarity,
                b.position,
                b.descentMs,
                b.materializeMs
            )
        }

        const onPlayerJoined = (peer: PlayerSnapshot) => {
            if (peer.id === useNetworkStore.getState().selfId) return
            net.addPeer(peer)
        }

        const onPlayerMoved = (b: PlayerMovedBroadcast) => {
            if (b.id === useNetworkStore.getState().selfId) return
            net.updatePeerTransform(b.id, b.position, b.rotation)
        }

        const onPlayerMoveClicked = (b: PlayerMoveClickedBroadcast) => {
            if (b.id === useNetworkStore.getState().selfId) return
            net.updatePeerTarget(b.id, b.target)
        }

        const onPlayerIndexUpdated = (b: PlayerIndexUpdatedBroadcast) => {
            net.updatePeerIndex(b.id, b.siliconIndex)
            // Si soy yo, también actualizo mi store local.
            if (b.id === useNetworkStore.getState().selfId) {
                usePlayerStore.getState().setSiliconIndex(b.siliconIndex)
            }
        }

        const onPlayerLeft = (b: PlayerLeftBroadcast) => {
            net.removePeer(b.id)
        }

        const onVoidReturn = () => {
            // Sprint 5 — el server confirmó la reconfiguración. Limpiamos
            // la fase local y dejamos al Tripulante en void; el selector
            // se desmonta solo cuando phase !== "void" y reaparece ahora
            // que volvió. Limpiamos también los peers fantasma del campo
            // que veníamos viendo (al volver materializado el ROOM_STATE
            // no vuelve a llegar — pero PLAYER_JOINED/SPAWN_EVENT sí).
            usePlayerStore.getState().returnToVoid()
        }

        socket.on("connect", onConnect)
        socket.on("disconnect", onDisconnect)
        socket.on("connect_error", onConnectError)
        socket.on(SOCKET_EVENTS.ROOM_STATE, onRoomState)
        socket.on(SOCKET_EVENTS.PLAYER_JOINED, onPlayerJoined)
        socket.on(SOCKET_EVENTS.PLAYER_MOVED, onPlayerMoved)
        socket.on(SOCKET_EVENTS.PLAYER_MOVE_CLICKED, onPlayerMoveClicked)
        socket.on(SOCKET_EVENTS.PLAYER_INDEX_UPDATED, onPlayerIndexUpdated)
        socket.on(SOCKET_EVENTS.PLAYER_LEFT, onPlayerLeft)
        socket.on(SOCKET_EVENTS.SPAWN_EVENT, onSpawnEvent)
        socket.on(SOCKET_EVENTS.VOID_RETURN, onVoidReturn)

        return () => {
            socket.off("connect", onConnect)
            socket.off("disconnect", onDisconnect)
            socket.off("connect_error", onConnectError)
            socket.off(SOCKET_EVENTS.ROOM_STATE, onRoomState)
            socket.off(SOCKET_EVENTS.PLAYER_JOINED, onPlayerJoined)
            socket.off(SOCKET_EVENTS.PLAYER_MOVED, onPlayerMoved)
            socket.off(SOCKET_EVENTS.PLAYER_MOVE_CLICKED, onPlayerMoveClicked)
            socket.off(SOCKET_EVENTS.PLAYER_INDEX_UPDATED, onPlayerIndexUpdated)
            socket.off(SOCKET_EVENTS.PLAYER_LEFT, onPlayerLeft)
            socket.off(SOCKET_EVENTS.SPAWN_EVENT, onSpawnEvent)
            socket.off(SOCKET_EVENTS.VOID_RETURN, onVoidReturn)
            disconnectSocket()
            net.reset()
            setConnected(false)
            setRoomId(null)
        }
    }, [token])

    return { connected, roomId, error, peerCount }
}
